/**
 * Wizard para criação de apólices com fluxo otimizado
 * Prioriza seleção de cliente e informações essenciais primeiro
 */

import React, { useState, useEffect } from 'react';
import { documentoService, seguradoraService, produtoService, clienteService, statusService, situacaoService } from '../../services/entitiesServices';
import { useNotifier } from '../ui/Notifier';

interface Cliente {
  id: string;
  nome: string;
  cpfCnpj: string;
  email?: string;
  telefone?: string;
}

interface BasicPolicyData {
  clienteId: string;
  seguradoraId: string;
  produtoId: string;
  statusId: string;
  situacaoId: string;
  inicioVigencia: string;
  fimVigencia: string;
  premioLiquido: number;
  quantidadeParcelas: number;
  percentualComissao: number;
  comissao: number; // Calculado automaticamente
}

interface ApoliceCreationWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (apoliceId: string) => void;
}

export const ApoliceCreationWizard: React.FC<ApoliceCreationWizardProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {

  const [loading, setLoading] = useState(false);
  const [catalogsLoading, setCatalogsLoading] = useState(true);
  
  // Step 1 - Basic Info
  const notifier = useNotifier();
  const [basicData, setBasicData] = useState<Partial<BasicPolicyData>>({
    quantidadeParcelas: 1,
    percentualComissao: 0,
    comissao: 0
  });
  
  // Catalogs
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [seguradoras, setSeguradoras] = useState<any[]>([]);
  const [produtos, setProdutos] = useState<any[]>([]);
  const [statuses, setStatuses] = useState<any[]>([]);
  const [situacoes, setSituacoes] = useState<any[]>([]);
  
  // Search states
  const [clienteSearch, setClienteSearch] = useState('');
  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null);
  const [premioLiquidoDisplay, setPremioLiquidoDisplay] = useState('');

  // Formatação de moeda
  const formatCurrencyDisplay = (value: number): string => {
    if (!value) return '';
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const parseCurrencyInput = (value: string): number => {
    if (!value) return 0;
    const cleanValue = value.replace(/\./g, '').replace(',', '.');
    return parseFloat(cleanValue) || 0;
  };

  const handlePremioLiquidoChange = (value: string) => {
    const numericValue = value.replace(/\D/g, '');
    const formattedValue = formatCurrencyDisplay(parseFloat(numericValue) / 100);
    setPremioLiquidoDisplay(formattedValue);
    handleBasicDataChange('premioLiquido', parseFloat(numericValue) / 100);
  };

  // Load catalogs
  useEffect(() => {
    if (isOpen) {
      loadCatalogs();
    }
  }, [isOpen]);

  // Test status service specifically
  const testStatusService = async () => {
    try {
      const result = await statusService.list({ limit: 100 });
      
      if (result?.data) {
        setStatuses(result.data);
      }
    } catch (error) {
      console.error('❌ Erro direto no statusService:', error);
    }
  };

  // Test individual services
  const testIndividualServices = async () => {
    try {
      const statusTest = await statusService.list({ limit: 10 });
    } catch (error) {
      console.error('❌ Erro no statusService:', error);
    }

    try {
      const situacaoTest = await situacaoService.list({ limit: 10 });
    } catch (error) {
      console.error('❌ Erro no situacaoService:', error);
    }
  };

  const loadCatalogs = async () => {
    setCatalogsLoading(true);
    try {
      try {
        const statusResult = await statusService.list({ limit: 1000 });
        setStatuses((statusResult?.data || []).sort((a: any, b: any) => 
          (a.nome || '').localeCompare(b.nome || '', 'pt-BR', { sensitivity: 'base' })
        ));
      } catch (statusError) {
        console.error('❌ Erro ao carregar status:', statusError);
      }

      const results = await Promise.allSettled([
        seguradoraService.list({ limit: 1000 }),
        produtoService.list({ limit: 1000 }),
        situacaoService.list({ limit: 1000 })
      ]);

      // Process results with error handling
      const [seguradoras, produtos, situacoes] = results;

      if (seguradoras.status === 'fulfilled') {
        const seguradorasData = seguradoras.value?.data || [];
        setSeguradoras(seguradorasData.sort((a, b) => 
          (a.nome || '').localeCompare(b.nome || '', 'pt-BR', { sensitivity: 'base' })
        ));
      } else {
        console.error('❌ Erro ao carregar seguradoras:', seguradoras.reason);
        setSeguradoras([]);
      }

      if (produtos.status === 'fulfilled') {
        const produtosData = produtos.value?.data || [];
        setProdutos(produtosData.sort((a, b) => 
          (a.nome || '').localeCompare(b.nome || '', 'pt-BR', { sensitivity: 'base' })
        ));
      } else {
        console.error('❌ Erro ao carregar produtos:', produtos.reason);
        setProdutos([]);
      }

      if (situacoes.status === 'fulfilled') {
        const situacoesData = situacoes.value?.data || [];
        setSituacoes(situacoesData.sort((a: any, b: any) => 
          (a.nome || '').localeCompare(b.nome || '', 'pt-BR', { sensitivity: 'base' })
        ));
      } else {
        console.error('❌ Erro ao carregar situações:', situacoes.reason);
        setSituacoes([]);
      }

    } catch (error) {
      console.error('❌ Erro geral ao carregar catálogos:', error);
      // Test individual services to identify the problem
      await testIndividualServices();
    } finally {
      setCatalogsLoading(false);
    }
  };

  // Search clientes
  const searchClientes = async (term: string) => {
    if (!term || term.length < 2) {
      setClientes([]);
      return;
    }

    try {
      const result = await clienteService.search(term, { limit: 50 });
      setClientes(result?.data || []);
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
      setClientes([]);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      searchClientes(clienteSearch);
    }, 300);

    return () => clearTimeout(debounce);
  }, [clienteSearch]);

  const handleClienteSelect = (cliente: Cliente) => {
    setSelectedCliente(cliente);
    setBasicData(prev => ({ ...prev, clienteId: cliente.id }));
    setClienteSearch(cliente.nome);
    setClientes([]);
  };

  const handleBasicDataChange = (field: keyof BasicPolicyData, value: any) => {
    setBasicData(prev => ({ ...prev, [field]: value }));
  };

  // Calcular comissão automaticamente quando mudar prêmio ou percentual
  useEffect(() => {
    const premioLiquido = basicData.premioLiquido || 0;
    const percentualComissao = basicData.percentualComissao || 0;
    const comissao = (premioLiquido * percentualComissao) / 100;
    
    setBasicData(prev => ({
      ...prev,
      comissao: parseFloat(comissao.toFixed(2))
    }));
  }, [basicData.premioLiquido, basicData.percentualComissao]);

  const validateStep1 = () => {
    return !!(
      basicData.clienteId &&
      basicData.seguradoraId &&
      basicData.produtoId &&
      basicData.statusId &&
      basicData.situacaoId &&
      basicData.inicioVigencia &&
      basicData.fimVigencia &&
      basicData.premioLiquido &&
      basicData.quantidadeParcelas &&
      typeof basicData.percentualComissao === 'number'
    );
  };

  // Função para converter data local para ISO com timezone SP
  const toISOWithSPTimezone = (dateString: string): string => {
    if (!dateString) return new Date().toISOString();
    
    try {
      // Abordagem mais robusta: criar data específica para SP
      // dateString vem no formato YYYY-MM-DD do input date
      const [year, month, day] = dateString.split('-').map(Number);
      
      // Criar data em SP (considerando que é meia-noite em SP)
      // SP = UTC-3, então 00:00 em SP = 03:00 UTC
      const spDate = new Date(Date.UTC(year, month - 1, day, 3, 0, 0, 0));
      
      const isoString = spDate.toISOString();
      
      return isoString;
    } catch (error) {
      console.error('❌ Erro na conversão de data:', error);
      return new Date().toISOString();
    }
  };

  const handleStep1Continue = async () => {
    if (!validateStep1()) {
      notifier.show({ message: 'Por favor, preencha todos os campos obrigatórios.', severity: 'warning' });
      return;
    }

    setLoading(true);
    try {
      // Converter datas para ISO com timezone SP
      const inicioVigenciaISO = toISOWithSPTimezone(basicData.inicioVigencia || '');
      const fimVigenciaISO = toISOWithSPTimezone(basicData.fimVigencia || '');
      const dataEmissaoISO = new Date().toISOString(); // Data atual em UTC

      // Create basic apólice
      const payload = {
        // Auto-generate numeroDocumento on backend
        cpfCnpjCliente: selectedCliente?.cpfCnpj,
        inicioVigencia: inicioVigenciaISO,
        fimVigencia: fimVigenciaISO,
        dataEmissao: dataEmissaoISO,
        premioLiquido: basicData.premioLiquido,
        quantidadeParcelas: basicData.quantidadeParcelas,
        percentualComissao: basicData.percentualComissao,
        comissao: basicData.comissao,
        seguradoraId: basicData.seguradoraId,
        produtoId: basicData.produtoId,
        statusId: basicData.statusId,
        situacaoId: basicData.situacaoId,
        tipoDocumento: 'apolice'
      };

      const response = await documentoService.create(payload);
      const apoliceId = response.data?.id;

      if (apoliceId) {
        // Success - close wizard and open full modal
        onSuccess(apoliceId);
        onClose();
      } else {
        throw new Error('ID da apólice não retornado');
      }
    } catch (error: any) {
      console.error('Erro ao criar apólice:', error);
      notifier.show({ message: `Erro ao criar apólice: ${error.message || 'Erro desconhecido'}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setBasicData({ quantidadeParcelas: 1, percentualComissao: 0, comissao: 0 });
    setSelectedCliente(null);
    setClienteSearch('');
    setClientes([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b" style={{ backgroundColor: '#5d54a4' }}>
          <h2 className="text-lg font-semibold text-white">Nova Apólice - Informações Básicas</h2>
          <button
            onClick={handleClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-160px)]">
          <div className="space-y-4">
            {/* Cliente - PRIMEIRO E PRINCIPAL */}
            <div className="p-4 rounded-lg border-2" style={{ backgroundColor: '#f3f1ff', borderColor: '#c7bfff' }}>
              <label className="block text-sm font-bold mb-2" style={{ color: '#5d54a4' }}>
                1. Cliente * (Obrigatório)
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Digite o nome ou CPF/CNPJ do cliente..."
                  value={clienteSearch}
                  onChange={(e) => setClienteSearch(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2"
                  style={{ borderColor: '#c7bfff', '--tw-ring-color': '#5d54a4' } as any}
                />
                
                {/* Dropdown de clientes */}
                {clientes.length > 0 && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-md shadow-lg z-10 max-h-48 overflow-y-auto">
                    {clientes.map((cliente) => (
                      <button
                        key={cliente.id}
                        onClick={() => handleClienteSelect(cliente)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-100 border-b border-gray-100 text-sm"
                      >
                        <div className="font-medium">{cliente.nome}</div>
                        <div className="text-gray-500 text-xs">{cliente.cpfCnpj}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {selectedCliente && (
                <div className="mt-2 p-2 rounded border" style={{ backgroundColor: '#e8f5e8', borderColor: '#a5d6a7' }}>
                  <div className="text-sm">
                    <strong>Cliente selecionado:</strong> {selectedCliente.nome}
                  </div>
                  <div className="text-xs text-gray-600">CPF/CNPJ: {selectedCliente.cpfCnpj}</div>
                </div>
              )}
            </div>

            {/* Seguradora */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                2. Seguradora *
              </label>
              <select
                value={basicData.seguradoraId || ''}
                onChange={(e) => handleBasicDataChange('seguradoraId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': '#5d54a4' } as any}
                disabled={catalogsLoading}
              >
                <option value="">
                  {catalogsLoading ? 'Carregando seguradoras...' : `Selecione uma seguradora (${seguradoras.length})`}
                </option>
                {seguradoras.map((seg) => (
                  <option key={seg.id} value={seg.id}>{seg.nome}</option>
                ))}
              </select>
            </div>

            {/* Produto */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                3. Produto *
              </label>
              <select
                value={basicData.produtoId || ''}
                onChange={(e) => handleBasicDataChange('produtoId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': '#5d54a4' } as any}
                disabled={catalogsLoading}
              >
                <option value="">
                  {catalogsLoading ? 'Carregando produtos...' : `Selecione um produto (${produtos.length})`}
                </option>
                {produtos.map((prod) => (
                  <option key={prod.id} value={prod.id}>{prod.nome}</option>
                ))}
              </select>
            </div>

            {/* Status e Situação */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  4. Status *
                </label>
                <select
                  value={basicData.statusId || ''}
                  onChange={(e) => handleBasicDataChange('statusId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': '#5d54a4' } as any}
                  disabled={catalogsLoading}
                >
                  <option value="">
                    {catalogsLoading ? 'Carregando status...' : `Selecione um status (${statuses.length})`}
                  </option>
                  {statuses.map((status) => (
                    <option key={status.id} value={status.id}>{status.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  5. Situação *
                </label>
                <select
                  value={basicData.situacaoId || ''}
                  onChange={(e) => handleBasicDataChange('situacaoId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': '#5d54a4' } as any}
                  disabled={catalogsLoading}
                >
                  <option value="">
                    {catalogsLoading ? 'Carregando situações...' : `Selecione uma situação (${situacoes.length})`}
                  </option>
                  {situacoes.map((situacao) => (
                    <option key={situacao.id} value={situacao.id}>{situacao.nome}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Vigência */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  6. Início da Vigência *
                </label>
                <input
                  type="date"
                  value={basicData.inicioVigencia || ''}
                  onChange={(e) => handleBasicDataChange('inicioVigencia', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': '#5d54a4' } as any}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  7. Fim da Vigência *
                </label>
                <input
                  type="date"
                  value={basicData.fimVigencia || ''}
                  onChange={(e) => handleBasicDataChange('fimVigencia', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': '#5d54a4' } as any}
                />
              </div>
            </div>

            {/* Prêmio e Percentual Comissão */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  8. Prêmio Líquido *
                </label>
                <input
                  type="text"
                  value={premioLiquidoDisplay}
                  onChange={(e) => handlePremioLiquidoChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': '#5d54a4' } as any}
                  placeholder="0,00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  9. Percentual Comissão * (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={basicData.percentualComissao || ''}
                  onChange={(e) => handleBasicDataChange('percentualComissao', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': '#5d54a4' } as any}
                  placeholder="0,00"
                />
              </div>
            </div>

            {/* Valor da Comissão (Calculado) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                10. Valor da Comissão (Calculado Automaticamente)
              </label>
              <input
                type="text"
                value={`R$ ${(basicData.comissao || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">
                Fórmula: R$ {(basicData.premioLiquido || 0).toFixed(2)} × {(basicData.percentualComissao || 0).toFixed(2)}% = R$ {(basicData.comissao || 0).toFixed(2)}
              </p>
            </div>

            {/* Parcelas */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                11. Quantidade de Parcelas *
              </label>
              <input
                type="number"
                min="1"
                max="24"
                value={basicData.quantidadeParcelas || 1}
                onChange={(e) => handleBasicDataChange('quantidadeParcelas', parseInt(e.target.value) || 1)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': '#5d54a4' } as any}
              />
            </div>

            {/* Info Box */}
            <div className="p-3 rounded border" style={{ backgroundColor: '#f8f4ff', borderColor: '#d4c5ff' }}>
              <div className="text-sm" style={{ color: '#5d54a4' }}>
                <strong>ℹ️ Próximo passo:</strong> Após salvar essas informações básicas, você poderá:
                <ul className="list-disc list-inside mt-1 text-xs">
                  <li>Ver o número do documento gerado automaticamente</li>
                  <li>Configurar as parcelas detalhadamente</li>
                  <li>Adicionar informações complementares</li>
                  <li>Configurar flags e opções avançadas</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between px-6 py-4 border-t bg-gray-50">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleStep1Continue}
            disabled={!validateStep1() || loading}
            className="px-6 py-2 text-sm text-white rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            style={{ backgroundColor: '#5d54a4' }}
          >
            {loading ? 'Salvando...' : 'Salvar e Continuar'}
          </button>
        </div>
      </div>
    </div>
  );
};